import axios from "axios";
import { useState, useEffect } from "react";
import { Alert, Container, TextField, Tooltip } from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import LoadingPanel from "../../../components/common/LoadingPanel";
import { Search } from "@mui/icons-material";

export default function AddOrEditClient() {
  const {
    clientModel,
    setClientModel,
    locations,
    setCity,
    cities,
    setCityFilterCode,
    getClientTypes,
    config,
    CLIENTS_API,
  } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState({ show: false });

  const getclientTypesData = async () => {
    const clientT = await getClientTypes();
    setClientTypes(clientT);
  };

  const getClientInfoByIdentification = async () => {
    try {
      if (clientModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: clientModel.Identification },
              Value: clientModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: clientModel.IdentificationType.Id },
              Value: clientModel.IdentificationType.Id,
            },
          ]),
          pagination: -1,
        },
      };
      const data = await axios.get(CLIENTS_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setClientModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_client_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getclientTypesData();
  }, []);

  const handleChange = (e) => {
    setClientModel({
      ...clientModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setClientModel({
        ...clientModel,
        IdentificationType: identificationType,
      });
    }
  };

  const handleStateChange = (currentState) => {
    if (!currentState) {
      setClientModel({
        ...clientModel,
        StateId: 0,
        State: null,
        CityId: 0,
        City: null,
      });
      return;
    }

    setClientModel({
      ...clientModel,
      StateId: currentState.Id,
      State: currentState,
      CityId: 0,
      City: null,
    });
  };

  const handleCityChange = (currentCity) => {
    if (!currentCity) {
      setClientModel({
        ...clientModel,
        CityId: 0,
        City: null,
      });
      return;
    }

    setClientModel({
      ...clientModel,
      CityId: currentCity.Id,
      City: currentCity,
    });

    setCityFilterCode(currentCity.Code);
  };

  const [states, setStates] = useState(null);
  const [citiesData, setCities] = useState(null);
  const [clientTypes, setClientTypes] = useState(null);

  const validateStateChange = () => {
    if (clientModel?.State) {
      clientModel.StateId = clientModel?.State.Id;
    }

    if (clientModel?.StateId > 0) {
      const selectedState = states?.find(
        (element) => element.Id === clientModel?.StateId
      );
      if (selectedState) {
        setCity(selectedState.Code);
      }
    }
  };

  useEffect(() => {
    if (cities && cities.length > 0 && clientModel?.City) {
      setCityFilterCode(clientModel.City.Code);
    }
  }, [cities]);

  useEffect(() => {
    validateStateChange();
  }, [clientModel?.StateId, states]);

  useEffect(() => {
    if (locations.Ok && locations.Data.length > 0) {
      setStates(locations.Data);
    }
  }, [locations]);

  useEffect(() => {
    if (cities.Ok && cities.Data.length > 0) {
      setCities(cities.Data);
    }
  }, [cities]);

  const handleClientTypeChange = (selectedClientType) => {
    if (!selectedClientType) {
      setClientModel({
        ...clientModel,
        clientTypeId: 0,
        ClientType: null,
      });
      return;
    }

    setClientModel({
      ...clientModel,
      clientTypeId: selectedClientType.Id,
      ClientType: selectedClientType,
    });
  };

  const isSearchAvailable = () =>
    clientModel?.Identification?.replaceAll(" ", "").length > 0;

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getClientInfoByIdentification();
    }
  };

  return (
    <div>
      <Container
        sx={{ paddingLeft: "0px !important;", paddingRight: "0px !important;" }}
        style={{ width: "100%" }}
      >
        <div className="w-full mt-2">
          <section className="flex justify-center content-center items-center ">
            {isLoading ? (
              <LoadingPanel />
            ) : (
              <section id="ReportData" className="w-full">
                <div className="flex gap-2 my-3 items-center">
                  <Combobox
                    isRequired
                    id="IdentificationType"
                    handleValueChange={handleIdentificationTypeChange}
                    fieldValue={clientModel?.IdentificationType ?? null}
                    fieldOptions={[
                      {
                        Id: "Cédula de ciudadanía",
                        Name: "Cédula de ciudadanía",
                      },
                      { Id: "Nit", Name: "Nit" },
                      { Id: "Pasaporte", Name: "Pasaporte" },
                      {
                        Id: "Cédula de extranjería",
                        Name: "Cédula de extranjería",
                      },
                      { Id: "Otro", Name: "Otro" },
                    ]}
                    fieldLabel={localize.identification_type}
                  />

                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Identification"
                    value={clientModel.Identification}
                    id="Identification"
                    label={localize.identification}
                    variant="outlined"
                    onChange={handleChange}
                    onKeyUp={onKeyUp}
                    autoComplete="off"
                  />

                  <Tooltip title={localize.client_search_tooltip}>
                    <button
                      disabled={!isSearchAvailable()}
                      style={{
                        color: isSearchAvailable() ? "#10366e" : "lightgray",
                      }}
                      onClick={getClientInfoByIdentification}
                    >
                      <Search
                        sx={{
                          width: "24px",
                          height: "24px",
                        }}
                      />
                    </button>
                  </Tooltip>
                </div>

                {showWarning?.show && (
                  <Alert className="mb-3" severity={showWarning.severity}>
                    <p className="report_little_text">{showWarning.message}</p>
                  </Alert>
                )}

                {/* client types */}
                <div className="flex mb-3">
                  <Combobox
                    isRequired
                    id="ClientType"
                    handleValueChange={handleClientTypeChange}
                    fieldValue={clientModel?.ClientType}
                    fieldOptions={clientTypes ?? []}
                    fieldLabel={localize.client_type}
                  />
                </div>

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Name"
                  id="Name"
                  value={clientModel.Name}
                  label={localize.name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
                {/* States */}
                <div className="flex justify-between my-3">
                  <Combobox
                    isRequired
                    id="State"
                    handleValueChange={handleStateChange}
                    fieldValue={clientModel?.State}
                    fieldOptions={states ?? []}
                    fieldLabel={localize.department}
                  />

                  {/* Cities */}
                  <div className="mx-2" />
                  <Combobox
                    isRequired
                    id="City"
                    handleValueChange={handleCityChange}
                    fieldValue={clientModel?.City}
                    fieldOptions={clientModel?.State ? citiesData ?? [] : []}
                    fieldLabel={localize.city}
                  />
                </div>

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Address"
                  id="Address"
                  value={clientModel.Address}
                  label={localize.address}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="mt-3" />

                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="AddressComplement"
                  id="AddressComplement"
                  value={clientModel.AddressComplement}
                  label={localize.address_complement}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="flex justify-between my-3">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="PhoneNumber"
                    id="PhoneNumber"
                    value={clientModel.PhoneNumber}
                    label={localize.phone_number}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  <div className="mx-2" />
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Email"
                    id="Email"
                    value={clientModel.Email}
                    label={localize.email}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  value={clientModel.ContactName}
                  name="ContactName"
                  id="ContactName"
                  label={localize.contact_name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </section>
            )}
          </section>
        </div>
      </Container>
    </div>
  );
}
